import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { fetchUpdates, UpdateItem } from "@/services/updatesService";

const filterOptions = ["All", "news", "offers", "app"];

export default function UpdatesSection() {
  const [selected, setSelected] = useState("All");
  const [updates, setUpdates] = useState<UpdateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [moreVisible, setMoreVisible] = useState({ next: false, prev: false });
  const [slidesPerView, setSlidesPerView] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUpdates().then((data) => {
      setUpdates(data);
      setLoading(false);
    });
  }, []);

  // Responsive slides per view
  useEffect(() => {
    const calcSlides = () => {
      if (window.innerWidth >= 1024) return setSlidesPerView(4);
      if (window.innerWidth >= 640) return setSlidesPerView(2);
      setSlidesPerView(1);
    };
    calcSlides();
    window.addEventListener("resize", calcSlides);
    return () => window.removeEventListener("resize", calcSlides);
  }, []);

  const filtered =
    selected === "All"
      ? updates
      : updates.filter(
          (item) => item.type.toLowerCase() === selected.toLowerCase(),
        );

  // First N updates for grid
  const gridUpdates = filtered.slice(0, slidesPerView);
  // Remaining updates for slider
  const sliderUpdates = filtered.slice(slidesPerView);

  // Track scroll position for arrows
  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    const updateArrows = () => {
      setMoreVisible({
        next: el.scrollLeft + el.offsetWidth < el.scrollWidth - 1,
        prev: el.scrollLeft > 0,
      });
    };
    el.addEventListener("scroll", updateArrows);
    updateArrows();
    return () => el.removeEventListener("scroll", updateArrows);
  }, [sliderUpdates.length, slidesPerView, selected]);

  // Simple scroll for slider
  const scroll = (dir: "left" | "right") => {
    if (!sliderRef.current) return;
    const card = sliderRef.current.querySelector(".update-card");
    if (!card) return;
    const cardWidth = (card as HTMLElement).offsetWidth + 16;
    sliderRef.current.scrollBy({
      behavior: "smooth",
      left: dir === "left" ? -cardWidth : cardWidth,
    });
  };

  // Modern shimmer for loading
  function ModernShimmer({
    className = "",
    height,
    width,
  }: {
    className?: string;
    height: string;
    width: string;
  }) {
    return (
      <div
        className={`relative overflow-hidden rounded-xl shadow-sm ${className}`}
        style={{ height, width }}
      >
        <div
          className="animate-shimmer absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-base-200 dark:via-base-100 dark:to-base-200"
          style={{ backgroundSize: "200% 100%" }}
        />
      </div>
    );
  }

  if (
    typeof document !== "undefined" &&
    !document.getElementById("modern-shimmer-style")
  ) {
    const style = document.createElement("style");
    style.id = "modern-shimmer-style";
    style.innerHTML = `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .animate-shimmer {
        animation: shimmer 1.5s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }

  return (
    <section className="bg-base-100 px-4 py-10 text-base-content">
      <div className="mx-auto max-w-7xl">
        <h2 className="mb-8 text-center text-3xl font-bold md:text-4xl">
          Latest Updates
        </h2>

        {/* Filter Buttons */}
        <div className="mb-10 flex flex-wrap justify-center gap-2">
          {filterOptions.map((opt) => (
            <button
              className={`btn btn-sm rounded-full transition-all duration-200 ${
                selected === opt
                  ? "btn-primary"
                  : "bg-base-200 text-base-content hover:bg-base-300"
              }`}
              key={opt}
              onClick={() => setSelected(opt)}
            >
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>

        {/* Grid: First N updates */}
        <div className={`grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4`}>
          {loading
            ? Array.from({ length: slidesPerView }).map((_, i) => (
                <ModernShimmer
                  className="mb-2"
                  height="260px"
                  key={i}
                  width="100%"
                />
              ))
            : Array.isArray(gridUpdates) && gridUpdates.map((item) => (
                <div
                  className="update-card flex flex-col rounded-xl bg-base-200 p-6 shadow-md transition-transform hover:scale-[1.02] hover:shadow-lg"
                  key={item.id}
                >
                  {item.image && (
                    <img
                      alt={item.title}
                      className="mb-3 h-32 w-full rounded-lg object-cover"
                      loading="lazy"
                      src={
                        item.image.startsWith("http")
                          ? item.image
                          : `${import.meta.env.VITE_API_STORAGE_URL}${item.image}`
                      }
                    />
                  )}
                  <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                    {item.type}
                  </span>
                  <h3 className="mt-2 text-lg font-bold">
                    {item.title && item.title.length > 25
                      ? item.title.slice(0, 25) + ".."
                      : item.title}
                  </h3>
                  <p className="text-sm text-base-content/70">
                    {item.subtitle && item.subtitle.length > 25
                      ? item.subtitle.slice(0, 25) + ".."
                      : item.subtitle || item.date}
                  </p>
                  <p className="mt-2 text-sm text-base-content/80">
                    {item.description && item.description.length > 70
                      ? item.description.slice(0, 70) + ".."
                      : item.description}
                  </p>
                  <button
                    className="btn btn-outline btn-sm mt-4 self-end"
                    onClick={() => navigate(`/updates/${item.id}`)}
                  >
                    Read More
                  </button>
                </div>
              ))}
        </div>

        {/* Slider: Remaining updates */}
        {sliderUpdates.length > 0 && (
          <div className="mt-8">
            <div
              className="flex gap-4 overflow-x-auto scroll-smooth pb-2"
              ref={sliderRef}
              style={{ scrollbarWidth: "none" }}
            >
              {loading
                ? Array.from({ length: sliderUpdates.length }).map((_, i) => (
                    <ModernShimmer
                      className="mb-2"
                      height="260px"
                      key={i}
                      width="260px"
                    />
                  ))
                : sliderUpdates?.map?.((item) => (
                    <div
                      className="update-card flex min-w-[260px] max-w-xs flex-shrink-0 flex-col rounded-xl bg-base-200 p-6 shadow-md"
                      key={item.id}
                    >
                      {item.image && (
                        <img
                          alt={item.title}
                          className="mb-3 h-32 w-full rounded-lg object-cover"
                          loading="lazy"
                          src={
                            item.image.startsWith("http")
                              ? item.image
                              : `${import.meta.env.VITE_API_STORAGE_URL}${item.image}`
                          }
                        />
                      )}
                      <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                        {item.type}
                      </span>
                      <h3 className="mt-2 text-lg font-bold">
                        {item.title && item.title.length > 25
                          ? item.title.slice(0, 25) + ".."
                          : item.title}
                      </h3>
                      <p className="text-sm text-base-content/70">
                        {item.subtitle && item.subtitle.length > 25
                          ? item.subtitle.slice(0, 25) + ".."
                          : item.subtitle || item.date}
                      </p>
                      <p className="mt-2 text-sm text-base-content/80">
                        {item.description && item.description.length > 70
                          ? item.description.slice(0, 70) + ".."
                          : item.description}
                      </p>
                      <button
                        className="btn btn-outline btn-sm mt-4 self-end"
                        onClick={() => navigate(`/updates/${item.id}`)}
                      >
                        Read More
                      </button>
                    </div>
                  ))}
            </div>
            {/* Arrows below slider */}
            <div className="mt-4 flex justify-center gap-4">
              {moreVisible.prev && (
                <button
                  aria-label="Previous"
                  className="rounded-full bg-base-100 p-2 shadow hover:bg-primary hover:text-white"
                  onClick={() => scroll("left")}
                  type="button"
                >
                  <ChevronLeft size={28} />
                </button>
              )}
              {moreVisible.next && (
                <button
                  aria-label="Next"
                  className="rounded-full bg-base-100 p-2 shadow hover:bg-primary hover:text-white"
                  onClick={() => scroll("right")}
                  type="button"
                >
                  <ChevronRight size={28} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* View More Button */}
        <div className="mt-8 text-center">
          <button
            className="btn btn-primary btn-lg"
            onClick={() => navigate("/pages/about-us/updates")}
          >
            View More Updates
          </button>
        </div>
      </div>
    </section>
  );
}
